
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

type ModelConfig = Database['public']['Tables']['model_configs']['Row'];
type ModelConfigInsert = Database['public']['Tables']['model_configs']['Insert'];
type ModelConfigUpdate = Database['public']['Tables']['model_configs']['Update'];

export const useModelManagement = () => {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchModels = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching all models for management...');
      const { data, error } = await supabase
        .from('model_configs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching models:', error);
        throw error;
      }
      
      console.log('Fetched models for management:', data);
      setModels(data || []);
    } catch (error) {
      console.error('Error fetching models:', error);
      toast.error('Error loading models');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateModel = async (id: string, updates: ModelConfigUpdate) => {
    try {
      console.log('Updating model:', id, 'with updates:', updates);
      
      const { data, error } = await supabase
        .from('model_configs')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select();

      if (error) {
        console.error('Error updating model:', error);
        throw error;
      }
      
      console.log('Model updated successfully:', data);
      toast.success(`Model ${updates.is_active !== undefined ? (updates.is_active ? 'enabled' : 'disabled') : 'updated'} successfully`);
      
      // Update local state immediately for better UX
      if (data && data[0]) {
        setModels(prev => prev.map(model => 
          model.id === id ? { ...model, ...data[0] } : model
        ));
      }
    } catch (error) {
      console.error('Error updating model:', error);
      toast.error('Error updating model');
    }
  };

  const addModel = async (model: ModelConfigInsert) => {
    try {
      console.log('Adding new model:', model);
      
      const { data, error } = await supabase
        .from('model_configs')
        .insert([model])
        .select();

      if (error) {
        console.error('Error adding model:', error);
        throw error;
      }
      
      console.log('Model added successfully:', data);
      toast.success('Model added successfully');
      
      // Add to local state immediately
      if (data && data[0]) {
        setModels(prev => [data[0], ...prev]);
      }
    } catch (error) {
      console.error('Error adding model:', error);
      toast.error('Error adding model');
    }
  };

  const deleteModel = async (id: string) => {
    try {
      console.log('Deleting model:', id);
      
      const { error } = await supabase
        .from('model_configs')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting model:', error);
        throw error;
      }
      
      console.log('Model deleted successfully');
      toast.success('Model deleted successfully');
      
      // Remove from local state immediately
      setModels(prev => prev.filter(model => model.id !== id));
    } catch (error) {
      console.error('Error deleting model:', error);
      toast.error('Error deleting model');
    }
  };

  useEffect(() => {
    fetchModels();

    // Set up real-time subscription for admin model management
    console.log('Setting up real-time subscription for model management...');
    const channel = supabase
      .channel(`model-configs-admin-${Date.now()}-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'model_configs' },
        (payload) => {
          console.log('Model config admin change detected:', payload);
          
          // Handle real-time updates
          if (payload.eventType === 'INSERT') {
            const newModel = payload.new as ModelConfig;
            setModels(prev => {
              const exists = prev.find(m => m.id === newModel.id);
              if (!exists) {
                return [newModel, ...prev];
              }
              return prev;
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedModel = payload.new as ModelConfig;
            setModels(prev => prev.map(model => 
              model.id === updatedModel.id ? updatedModel : model
            ));
          } else if (payload.eventType === 'DELETE') {
            const deletedModel = payload.old as ModelConfig;
            setModels(prev => prev.filter(model => model.id !== deletedModel.id));
          }
        }
      )
      .subscribe((status) => {
        console.log('Admin real-time subscription status:', status);
      });

    return () => {
      console.log('Cleaning up admin real-time subscription...');
      try {
        supabase.removeChannel(channel);
      } catch (error) {
        console.error('Error cleaning up model management subscription:', error);
      }
    };
  }, [fetchModels]);

  return {
    models,
    loading,
    updateModel,
    addModel,
    deleteModel,
    refetch: fetchModels
  };
};
